'use client';

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Crown, 
  ArrowRight, 
  CheckCircle,
  Clock,
  Calendar,
  Lightning,
  HardDrive,
  Users,
  XCircle,
  Warning,
  Spinner
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { subscriptionApi, paymentApi } from '../../services/api';
import { useToastActions } from '../../components/ui/Toast';
import { useLanguageStore } from '../../stores/languageStore';
import { useRuntimeConfigStore } from '../../stores/runtimeConfigStore';

// 支付方式类型
type PaymentProvider = 'wechatpay' | 'lemonsqueezy' | 'creem' | 'paddle' | 'stripe' | 'manual';


interface PlanConfig {
  plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
  name: string;
  description: string;
  monthlyPriceUSD: number;
  yearlyPriceUSD: number;
  monthlyPriceCNY: number;
  yearlyPriceCNY: number;
  maxWorkflowExecutions: number;
  maxStorageBytes: number;
  maxTeamsPerUser: number;
  maxMembersPerTeam: number;
  features: string[];
  visible: boolean;
  popular: boolean;
}

interface UsageStats {
  currentMonthExecutions: number;
  maxWorkflowExecutions: number;
  currentStorageBytes: number;
  maxStorageBytes: number;
  executionUsagePercent: number;
  storageUsagePercent: number;
}

// 订阅状态信息（包含支付方式）
interface SubscriptionStatus {
  active: boolean;
  hasSubscription: boolean;
  provider?: PaymentProvider;
  subscriptionId?: string;
  plan?: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
  status?: string;
  currentPeriodEnd?: string;
  nextBilledAt?: string;
  canceledAt?: string;
}

export function BillingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscription, fetchSubscription } = useSubscriptionStore();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const { isRegionCN } = useRuntimeConfigStore();

  const {language} = useLanguageStore();

  const [planConfigs, setPlanConfigs] = useState<PlanConfig[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('en');
  
  // 取消订阅相关状态
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [lang]);


  const correctLanguage = useCallback((language: string) : string=> {
    switch(language) {
      case 'zh':
      case 'zh-CN':
          return 'zh';
      case 'en':
      case 'en-US':
        return 'en';
      case 'ja':
        return 'ja';
    }

    return 'en';
  }, []);

  useEffect(() => {
    const l = correctLanguage(language);
    console.log('language change', language);
    setLang(l);
  }, [language, correctLanguage])

  

  const loadData = async () => {
    try {
      setLoading(true);
      await fetchSubscription();
      
      // 获取计划配置
      const plansResponse = await subscriptionApi.getPlanConfigs(lang);
      if (plansResponse.code === 0 && plansResponse.data) {
        setPlanConfigs(plansResponse.data);
      }
      
      // 获取用量统计
      const usageResponse = await subscriptionApi.getUsageStats();
      if (usageResponse.code === 0 && usageResponse.data) {
        setUsageStats(usageResponse.data);
      }
      
      // 获取订阅状态（包含支付方式信息）
      const statusResponse = await subscriptionApi.getCreemSubscriptionStatus();
      if (statusResponse.code === 0 && statusResponse.data) {
        setSubscriptionStatus(statusResponse.data as SubscriptionStatus);
      }
    } catch (err) {
      console.error('Failed to load billing data:', err);
      toastError(t('billing.loadFailed') || '加载订阅信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/upgrade');
  };

  // 获取支付方式显示名称
  const getPaymentProviderName = (provider?: PaymentProvider) => {
    switch (provider) {
      case 'wechatpay': return t('billing.wechatPay') || '微信支付';
      case 'lemonsqueezy': return 'LemonSqueezy';
      case 'creem': return 'Creem';
      case 'paddle': return 'Paddle';
      case 'stripe': return 'Stripe';
      default: return t('billing.manual') || '手动';
    }
  };

  // 处理取消订阅
  const handleCancelSubscription = async () => {
    if (!subscriptionStatus?.hasSubscription) return;
    
    setCancelLoading(true);
    try {
      const provider = subscriptionStatus.provider;
      let response;
      
      // 根据支付方式调用不同的取消 API
      switch (provider) {
        case 'lemonsqueezy':
          response = await subscriptionApi.cancelLemonSqueezySubscription();
          break;
        case 'creem':
        case 'paddle':
        case 'stripe':
          // 使用统一的支付取消接口，传入 provider 参数
          response = await paymentApi.cancelSubscription(provider);
          break;
        case 'wechatpay':
          // 微信支付需要终止委托代扣协议
          if (subscription?.plan && ['pro', 'team', 'enterprise'].includes(subscription.plan)) {
            response = await subscriptionApi.terminateWechatpayContract(subscription.plan as 'pro' | 'team' | 'enterprise');
          }
          break;
        default:
          // 默认使用标准取消接口
          response = await paymentApi.cancelSubscription();
      }
      
      if (response && (response.code === 0 || response.code === 200)) {
        toastSuccess(t('billing.cancelSuccess') || '订阅已取消成功');
        setShowCancelDialog(false);
        await fetchSubscription();
        await loadData();
      } else {
        toastError(response?.message || t('billing.cancelFailed') || '取消订阅失败');
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      toastError(t('billing.cancelFailed') || '取消订阅失败');
    } finally {
      setCancelLoading(false);
    }
  };

  const getCurrentPlanConfig = () => {
    return planConfigs.find(p => p.plan === subscription?.plan);
  };

  const formatStorage = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // 调试日志
  useEffect(() => {
    if (subscription) {
      console.log('[Billing] Subscription:', subscription);
      console.log('[Billing] expiresAt:', subscription.expiresAt);
    }
  }, [subscription]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  const currentPlan = getCurrentPlanConfig();
  const isFree = subscription?.plan === 'basic' || subscription?.plan === 'free';

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl font-bold text-zinc-900">{t('billing.title') || '订阅管理'}</h1>
          <p className="text-zinc-500 mt-1">{t('billing.subtitle') || '管理您的订阅计划和账单'}</p>
        </motion.div>

        {/* Current Plan Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 mb-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isFree ? 'bg-zinc-100' : 'bg-amber-100'
                }`}>
                  <Crown className={`w-6 h-6 ${isFree ? 'text-zinc-500' : 'text-amber-600'}`} weight="fill" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-zinc-900">
                    {currentPlan?.name || subscription?.plan || 'Free'}
                  </h2>
                  <p className="text-sm text-zinc-500">
                    {isFree 
                      ? (t('billing.freePlanDesc') || '免费计划，适合个人轻度使用')
                      : (t('billing.paidPlanDesc') || '付费计划，享受更多功能')
                    }
                  </p>
                </div>
              </div>
              
              {subscription && (
                <div className="flex items-center gap-4 mt-4 text-sm text-zinc-600">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-zinc-400" />
                    <span>{t('billing.startedAt') || '开始于'}: {formatDate(subscription.startedAt || subscription.createdAt)}</span>
                  </div>
                  {!isFree && (
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-zinc-400" />
                      <span>{t('billing.expiresAt') || '到期于'}: {formatDate(subscription.expiresAt)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {/* 取消订阅按钮 - 仅付费计划显示 */}
              {!isFree && subscriptionStatus?.hasSubscription && (
                <button
                  onClick={() => setShowCancelDialog(true)}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                >
                  <XCircle className="w-4 h-4" />
                  {t('billing.cancelSubscription') || '取消订阅'}
                </button>
              )}
              {/* 升级按钮 - 非CN区域显示 */}
              {!isRegionCN() && (
                <button
                  onClick={handleUpgrade}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  {isFree ? (t('billing.upgrade') || '升级') : (t('billing.changePlan') || '更改计划')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Usage Overview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          {/* Executions */}
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Lightning className="w-5 h-5 text-blue-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">{t('billing.executions') || '表格流执行'}</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {usageStats?.currentMonthExecutions || 0} / {usageStats?.maxWorkflowExecutions === -1 ? '∞' : (usageStats?.maxWorkflowExecutions || 0)}
                </p>
              </div>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(usageStats?.executionUsagePercent || 0, 100)}%`,
                  backgroundColor: (usageStats?.executionUsagePercent || 0) > 90 ? '#ef4444' : '#3b82f6'
                }}
              />
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              {t('billing.usedPercent', { percent: Math.round(usageStats?.executionUsagePercent || 0) }) || `已使用 ${Math.round(usageStats?.executionUsagePercent || 0)}%`}
            </p>
          </div>

          {/* Storage */}
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-purple-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">{t('billing.storage') || '数据存储'}</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {formatStorage(usageStats?.currentStorageBytes || 0)} / {usageStats?.maxStorageBytes === -1 ? '∞' : formatStorage(usageStats?.maxStorageBytes || 0)}
                </p>
              </div>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(usageStats?.storageUsagePercent || 0, 100)}%`,
                  backgroundColor: (usageStats?.storageUsagePercent || 0) > 90 ? '#ef4444' : '#a855f7'
                }}
              />
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              {t('billing.usedPercent', { percent: Math.round(usageStats?.storageUsagePercent || 0) }) || `已使用 ${Math.round(usageStats?.storageUsagePercent || 0)}%`}
            </p>
          </div>

          {/* Team */}
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">{t('billing.team') || '团队配额'}</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {currentPlan?.maxTeamsPerUser === -1 ? '∞' : (currentPlan?.maxTeamsPerUser || 0)} {t('billing.teams') || '团队'}
                </p>
              </div>
            </div>
            <div className="text-xs text-zinc-400 mt-2">
              {currentPlan?.maxMembersPerTeam === -1 
                ? (t('billing.unlimitedMembers') || '每团队无限成员')
                : t('billing.maxMembersPerTeam', { count: currentPlan?.maxMembersPerTeam || 0 })}
            </div>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6"
        >
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">{t('billing.currentFeatures') || '当前计划功能'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentPlan?.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" weight="fill" />
                <span className="text-sm text-zinc-700">{feature}</span>
              </div>
            )) || (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" weight="fill" />
                <span className="text-sm text-zinc-700">{t('billing.basicFeatures') || '基础功能'}</span>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* 取消订阅确认对话框 */}
      <Dialog.Root open={showCancelDialog} onOpenChange={(open) => !open && setShowCancelDialog(false)}>
        <Dialog.Portal>
          <AnimatePresence>
            {showCancelDialog && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-zinc-900 mb-2">
                      <Warning className="w-5 h-5 text-amber-500" weight="fill" />
                      {t('billing.cancelConfirmTitle') || '确认取消订阅'}
                    </Dialog.Title>
                    <Dialog.Description className="text-sm text-zinc-600 mb-4 leading-relaxed">
                      {t('billing.cancelConfirmDesc') || '取消订阅后，您仍可以使用付费功能直到当前计费周期结束。取消后不会自动续费，您的订阅将在到期后自动降级为免费计划。'}
                    </Dialog.Description>
                    
                    {/* 显示支付方式信息 */}
                    {subscriptionStatus?.provider && (
                      <div className="bg-zinc-50 rounded-lg p-3 mb-4">
                        <p className="text-xs text-zinc-500 mb-1">{t('billing.paymentProvider') || '支付方式'}</p>
                        <p className="text-sm font-medium text-zinc-900">{getPaymentProviderName(subscriptionStatus.provider)}</p>
                      </div>
                    )}

                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowCancelDialog(false)}
                        disabled={cancelLoading}
                        className="px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {t('common.cancel') || '取消'}
                      </button>
                      <button
                        onClick={handleCancelSubscription}
                        disabled={cancelLoading}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {cancelLoading && <Spinner className="w-4 h-4 animate-spin" />}
                        {t('billing.confirmCancel') || '确认取消'}
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default BillingPage;
